import React, { useState } from "react";
import { Menu } from "@mui/base/Menu";
import { MenuButton as BaseMenuButton } from "@mui/base/MenuButton";
import { MenuItem } from "@mui/material";
import { styled } from "@mui/system";
import { CssTransition } from "@mui/base/Transitions";
import { PopupContext } from "@mui/base/Unstable_Popup";
import { Dropdown } from "@mui/base";
import PropTypes from "prop-types";
import { AppBar, Box, IconButton, Popover, TextField } from "@mui/material";
import { SidebarStyle } from "../Config/Style";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import MenuIcon from "@mui/icons-material/Menu";
import { Data } from "../Config/Static";
import BreadcrumbsComp from "./Breadcrumbs";

const Header = ({ open, setOpen, isMobile }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleSidebarToggle = () => {
    setOpen(!open);
  };

  const blue = {
    50: "#F0F7FF",
    100: "#C2E0FF",
    200: "#99CCF3",
    300: "#66B2FF",
    400: "#3399FF",
    500: "#007FFF",
    600: "#0072E6",
    700: "#0059B3",
    800: "#004C99",
    900: "#003A75",
  };

  const grey = {
    50: "#F3F6F9",
    100: "#E5EAF2",
    200: "#DAE2ED",
    300: "#C7D0DD",
    400: "#B0B8C4",
    500: "#9DA8B7",
    600: "#6B7A90",
    700: "#434D5B",
    800: "#303740",
    900: "#1C2025",
  };

  const Listbox = styled("ul")(
    ({ theme }) => `
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 0.875rem;
    box-sizing: border-box;
    padding: 6px;
    margin: 12px 0;
    min-width: 200px;
    border-radius: 12px;
    overflow: auto;
    outline: 0;
    background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
    border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
    color: ${theme.palette.mode === "dark" ? grey[300] : grey[900]};
    box-shadow: 0 4px 30px ${
      theme.palette.mode === "dark" ? grey[900] : grey[200]
    };
    z-index: 1;
  
    .closed & {
      opacity: 0;
      transform: scale(0.95, 0.8);
      transition: opacity 200ms ease-in, transform 200ms ease-in;
    }
    
    .open & {
      opacity: 1;
      transform: scale(1, 1);
      transition: opacity 100ms ease-out, transform 100ms cubic-bezier(0.43, 0.29, 0.37, 1.48);
    }
  
    .placement-top & {
      transform-origin: bottom;
    }
  
    .placement-bottom & {
      transform-origin: top;
    }
    `
  );

  const AnimatedListbox = React.forwardRef(function AnimatedListbox(
    props,
    ref
  ) {
    const { ownerState, ...other } = props;
    const popupContext = React.useContext(PopupContext);

    if (popupContext == null) {
      throw new Error(
        "The `AnimatedListbox` component cannot be rendered outside a `Popup` component"
      );
    }

    const verticalPlacement = popupContext.placement.split("-")[0];

    return (
      <CssTransition
        className={`placement-${verticalPlacement}`}
        enterClassName="open"
        exitClassName="closed"
      >
        <Listbox {...other} ref={ref} />
      </CssTransition>
    );
  });

  AnimatedListbox.propTypes = {
    ownerState: PropTypes.object.isRequired,
  };

  const MenuButton = styled(BaseMenuButton)(
    ({ theme }) => `
        font-weight: 600;
        font-size: 0.875rem;
        border-radius: 8px;
        transition: all 150ms ease;
        cursor: pointer;
        background: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
        border: none;
        color: ${theme.palette.mode === "dark" ? grey[200] : grey[900]};
      
        &:hover {
          background: ${theme.palette.mode === "dark" ? grey[800] : grey[50]};
          border-color: ${
            theme.palette.mode === "dark" ? grey[600] : grey[300]
          };
        }
      
        &:active {
          background: ${theme.palette.mode === "dark" ? grey[700] : grey[100]};
        }
      
        &:focus-visible {
          box-shadow: 0 0 0 4px ${
            theme.palette.mode === "dark" ? blue[300] : blue[200]
          };
          outline: none;
        }
        `
  );

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        ...SidebarStyle.appbar,
        backgroundColor: "transparent",
      }}
    >
      <Box sx={SidebarStyle.header}>
        {isMobile ? (
          <Box
            sx={{
              flexGrow: 1,
              alignItems: "center",
              justifyContent: "space-between",
              display: "flex",
              mr: 2,
              gap: 0.5,
              width: "100%",
            }}
          >
            <IconButton
              aria-label="toggle sidebar"
              onClick={handleSidebarToggle}
              sx={{
                mr: 1,
                color: "#000",
                "& .MuiSvgIcon-root": {
                  fontSize: "24px",
                },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src={require("../assets/logo.jpg")}
                alt="logo"
                sx={{ width: { xs: "60px", md: "100px" } }}
              />
            </Box>
            <TextField
              size="small"
              InputProps={{
                endAdornment: <SearchOutlinedIcon />,
              }}
              placeholder="Search...."
              sx={{
                flexGrow: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "25px",
                },
              }}
            />

            {/* New Popover Dropdown for Mobile */}
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  maxWidth: "90vw",
                  width: "fit-content",
                  overflowX: "auto",
                  zIndex: 9999,
                },
              }}
            >
              <MenuItem onClick={() => setAnchorEl(null)}>
                Knowledge Base
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>Favourite</MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>Create New</MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>
                Notifications
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>Profile</MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>Log out</MenuItem>
            </Popover>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box
                component="img"
                src={require("../assets/logo.jpg")}
                alt="logo"
                sx={{ width: { xs: "60px", md: "70px" } }}
              />
            </Box>
            <Box sx={SidebarStyle.headerbox}>
              {Data.header.map((IconComponent, index) => (
                <Box key={index} sx={{ mx: 1, cursor: "pointer" }}>
                  {IconComponent}
                </Box>
              ))}

              <TextField
                size="small"
                InputProps={{
                  endAdornment: <SearchOutlinedIcon />,
                }}
                placeholder="Search...."
                sx={{
                  minWidth: "140px",
                  mx: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "25px",
                  },
                }}
              />
              <Dropdown>
                <MenuButton
                  sx={{ display: "flex", alignItems: "center", mr: 2 }}
                >
                  <img
                    width={"26px"}
                    height={"26px"}
                    src={require("../assets/profile.png")}
                    alt="icon"
                  />
                  <ArrowDropDownIcon />
                </MenuButton>
                <Menu
                  slots={{ listbox: AnimatedListbox }}
                  slotProps={{
                    root: {
                      placement: "bottom-end",
                    },
                  }}
                  style={{ zIndex: 999999 }}
                >
                  <MenuItem sx={{ cursor: "pointer" }}>Profile</MenuItem>
                  <MenuItem sx={{ cursor: "pointer" }}>Log out</MenuItem>
                </Menu>
              </Dropdown>
            </Box>
          </>
        )}
      </Box>
      <BreadcrumbsComp />
    </AppBar>
  );
};

export default Header;
